import {testIdProps} from '@github-ui/test-id-props'
import {ChevronRightIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {memo} from 'react'

import {ViewType} from '../../helpers/view-type'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import {type ConfigurationOption, optionsForViewType} from './configuration'

const ListItem = memo(function ListItem({
  title,
  testId,
  children,
  disabled,
  icon: Icon,
  onSelect,
}: React.PropsWithChildren<
  Omit<ConfigurationOption, 'TextContent'> & {
    onSelect: (option: ConfigurationOption['title']) => void
    disabled?: boolean
  }
>) {
  return (
    <ActionList.Item
      disabled={disabled}
      {...testIdProps(`view-options-menu-item-${testId}`)}
      onSelect={() => onSelect(title)}
    >
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>

      {children}

      <ActionList.TrailingVisual>
        <ChevronRightIcon />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
})

export const ConfigurationItems = memo(function ConfigurationItems({
  onSelect,
}: {
  onSelect: (option: ConfigurationOption['title']) => void
}) {
  const {viewType} = useViewType()
  const {currentView} = useViews()
  const enabledFeatures = useEnabledFeatures()

  const options = optionsForViewType[viewType]
  if (!options || !currentView) return null

  return (
    <ActionList.Group title="Configuration" variant="subtle">
      {options.map(option => {
        if (option.hideIf({currentView, enabledFeatures})) return null
        const disabled =
          enabledFeatures.memex_table_without_limits &&
          ((option.title === 'Slice by' && !enabledFeatures.memex_mwl_slice_by) ||
            (option.title === 'Group by' && viewType === ViewType.Board))

        return (
          <ListItem {...option} disabled={disabled} key={option.title} onSelect={onSelect}>
            <option.TextContent />
          </ListItem>
        )
      })}
    </ActionList.Group>
  )
})

export const ConfigurationItemMenus = memo(function ConfigurationItemMenus({
  currentMenu,
  setShowSubMenu,
  anchorRef,
}: {
  currentMenu: ConfigurationOption['title'] | null
  setShowSubMenu: (next: ConfigurationOption['title'] | null) => void
  anchorRef: React.RefObject<HTMLElement>
}) {
  const {viewType} = useViewType()
  const {currentView} = useViews()
  const enabledFeatures = useEnabledFeatures()

  const options = optionsForViewType[viewType]
  if (!options || !currentView) return null

  return (
    <>
      {options.map(({MenuComponent, title, ...option}) => {
        if (option.hideIf({currentView, enabledFeatures})) return null

        return (
          <MenuComponent
            key={title}
            anchorRef={anchorRef}
            open={currentMenu === title}
            setOpen={nextOpen => {
              setShowSubMenu(nextOpen ? title : null)
            }}
          />
        )
      })}
    </>
  )
})

try{ ListItem.displayName ||= 'ListItem' } catch {}
try{ ConfigurationItems.displayName ||= 'ConfigurationItems' } catch {}
try{ ConfigurationItemMenus.displayName ||= 'ConfigurationItemMenus' } catch {}